import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, Save, Plus, Trash2, Youtube, Play, UploadCloud, Film, Image as ImageIcon, Loader2, AlertTriangle } from "lucide-react";
import { useUser } from "../context/UserContext";
import { getCreator, updateCreator, uploadPortfolio, deleteMyAccount } from "../lib/api";
import { GAMES, QUALITIES, SERVICES, LANGUAGES, DELIVERY_SCOPES } from "../lib/constants";
import { extractYouTubeId, youtubeThumb } from "../lib/youtube";
import UpsellsManager from "../components/UpsellsManager";
import InfoTip from "../components/InfoTip";
import { toast } from "sonner";

const isVideoUrl = (u) => /\.(mp4|webm|mov|mkv|avi)(\?|$)/i.test(u || "");
const isImageUrl = (u) => /\.(jpg|jpeg|png|webp|gif)(\?|$)/i.test(u || "");
const isUploadedFile = (u) => typeof u === "string" && u.startsWith("/api/uploads/files/");

const absUrl = (u) => u;  // same-origin in all environments

const GLYPHS = ["❄", "⚡", "☾", "◉", "◬", "⟁", "✦", "△", "◈", "◆", "◊"];

export default function Settings() {
  const { user, setUser, signOut } = useUser();
  const nav = useNavigate();
  const [form, setForm] = useState(null);
  const [upsells, setUpsells] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (user?.role !== "creator") { setLoading(false); return; }
    getCreator(user.id)
      .then((c) => {
        setForm({
          bio: c.bio || "",
          glyph: c.glyph || "◈",
          avatar_url: c.avatar_url || "",
          games: c.games || [],
          quality: c.quality || "1080p 60fps",
          has_mic: !!c.has_mic,
          has_facecam: !!c.has_facecam,
          languages: c.languages || [],
          services: c.services || [],
          price: c.price ?? 50,
          avg_delivery_days: c.avg_delivery_days ?? 7,
          delivery_scope: c.delivery_scope || "record_edit",
          portfolio_urls: (c.portfolio || []).map((p) => p.url).filter(Boolean).length
            ? (c.portfolio || []).map((p) => p.url).filter(Boolean)
            : [""],
        });
        setUpsells(Array.isArray(c.upsells) ? c.upsells : []);
      })
      .catch(() => setForm(null))
      .finally(() => setLoading(false));
  }, [user?.id, user?.role]);

  if (user?.role !== "creator") {
    return (
      <div data-testid="settings-page" className="max-w-3xl mx-auto px-6 py-12">
        <div className="cc-overline">Settings</div>
        <h1 className="text-3xl font-semibold tracking-tight text-white mt-2 mb-6">Company account</h1>
        <div data-testid="settings-company-notice" className="cc-surface rounded-2xl p-6">
          <div className="text-sm text-[#a0a0b0]">
            Creator settings are only available to creator accounts. Switch accounts from the top-right menu to try the creator flow.
          </div>
        </div>
      </div>
    );
  }

  if (loading || !form) return <div className="max-w-3xl mx-auto px-6 py-12 text-[#606070]">Loading…</div>;

  const toggleList = (key, v) =>
    setForm((f) => ({ ...f, [key]: f[key].includes(v) ? f[key].filter((x) => x !== v) : [...f[key], v] }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const updated = await updateCreator(user.id, {
        ...form,
        portfolio_urls: form.portfolio_urls.filter((u) => u.trim()),
      });
      toast.success("Profile updated");
      setUser({ ...user, display_name: updated.username, glyph: updated.glyph, avatar_url: updated.avatar_url });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="settings-page" className="max-w-4xl mx-auto px-6 py-12">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="cc-overline">Creator settings</div>
          <h1 className="text-3xl font-semibold tracking-tight text-white mt-2">Profile & Listing</h1>
          <p className="text-sm text-[#a0a0b0] mt-2">Everything here is editable anytime — even after your listing is live.</p>
        </div>
        <button
          data-testid="settings-view-public-btn"
          onClick={() => nav(`/creators/${user.id}`)}
          className="text-xs text-[#00ff88] hover:underline inline-flex items-center gap-1"
        >
          View public profile →
        </button>
      </div>

      <form onSubmit={save} className="space-y-6">
        {/* Avatar */}
        <Section title="Profile picture & identity">
          <div className="flex items-center gap-5 flex-wrap">
            <div className="relative">
              <div className="w-24 h-24 rounded-2xl overflow-hidden border border-[#15152a] bg-[#06060c] flex items-center justify-center">
                {form.avatar_url ? (
                  <img src={form.avatar_url} alt="avatar" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                ) : (
                  <span className="text-3xl text-[#00ff88]">{form.glyph}</span>
                )}
              </div>
              <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-[#00ff88] text-[#06060c] flex items-center justify-center">
                <Camera size={14} />
              </div>
            </div>
            <div className="flex-1 min-w-[240px]">
              <div className="cc-overline mb-1.5">Avatar URL</div>
              <input
                data-testid="settings-avatar-url"
                value={form.avatar_url}
                onChange={(e) => setForm({ ...form, avatar_url: e.target.value })}
                placeholder="https://…/avatar.jpg"
                className="w-full bg-[#06060c] border border-[#15152a] rounded-lg px-3 py-2 text-sm text-white font-mono cc-focus-ring"
              />
              <div className="text-[11px] text-[#606070] mt-1">Paste any image URL (Imgur, Unsplash, your CDN).</div>
            </div>
          </div>

          <Field label="Glyph (if no avatar)">
            <div className="flex flex-wrap gap-1.5">
              {GLYPHS.map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => setForm({ ...form, glyph: g })}
                  data-testid={`settings-glyph-${g}`}
                  className={`w-9 h-9 rounded-md border text-[#00ff88] text-lg ${form.glyph === g ? "border-[#00ff88]/40 bg-[#00ff88]/5" : "border-[#15152a] bg-[#06060c] hover:border-[#1f1f3a]"}`}
                >
                  {g}
                </button>
              ))}
            </div>
          </Field>

          <Field label="Bio">
            <textarea
              data-testid="settings-bio"
              rows={3}
              value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value })}
              className="w-full bg-[#06060c] border border-[#15152a] rounded-lg p-3 text-sm text-white cc-focus-ring"
            />
          </Field>
        </Section>

        <Section title="Games & services">
          <Field label="Games">
            <Chips options={GAMES} selected={form.games} onToggle={(v) => toggleList("games", v)} testPrefix="settings-games" />
          </Field>
          <Field label="Services offered">
            <Chips options={SERVICES} selected={form.services} onToggle={(v) => toggleList("services", v)} testPrefix="settings-services" />
          </Field>
          <Field label="Languages">
            <Chips options={LANGUAGES} selected={form.languages} onToggle={(v) => toggleList("languages", v)} testPrefix="settings-langs" />
          </Field>
        </Section>

        <Section title="Equipment & quality">
          <Field label="Video quality">
            <select
              data-testid="settings-quality"
              value={form.quality}
              onChange={(e) => setForm({ ...form, quality: e.target.value })}
              className="bg-[#06060c] border border-[#15152a] rounded-lg px-3 py-2 text-sm text-white cc-focus-ring"
            >
              {QUALITIES.map((q) => <option key={q} className="bg-[#06060c]">{q}</option>)}
            </select>
          </Field>
          <div className="flex items-center gap-8">
            <Toggle label="Microphone" checked={form.has_mic} onChange={(v) => setForm({ ...form, has_mic: v })} testId="settings-mic" />
            <Toggle label="Facecam" checked={form.has_facecam} onChange={(v) => setForm({ ...form, has_facecam: v })} testId="settings-facecam" />
          </div>
        </Section>

        <Section title="Delivery">
          <Field label="What's included in your service?">
            <div className="grid sm:grid-cols-3 gap-2">
              {DELIVERY_SCOPES.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setForm({ ...form, delivery_scope: s.key })}
                  data-testid={`settings-scope-${s.key}`}
                  className={`text-left rounded-lg p-3 border transition-colors ${
                    form.delivery_scope === s.key
                      ? "border-[#00ff88]/40 bg-[#00ff88]/5"
                      : "border-[#15152a] bg-[#06060c] hover:border-[#1f1f3a]"
                  }`}
                >
                  <div className={`text-sm font-semibold ${form.delivery_scope === s.key ? "text-[#00ff88]" : "text-white"} flex items-center gap-1`}>
                    {s.key === "full" && <Youtube size={12} />}
                    {s.label}
                  </div>
                  <div className="text-[11px] text-[#606070] mt-0.5">{s.sub}</div>
                </button>
              ))}
            </div>
          </Field>
          <Field label="Average delivery time (days)">
            <div className="flex items-center gap-2">
              <input
                data-testid="settings-delivery-days"
                type="number"
                min="1"
                max="60"
                value={form.avg_delivery_days}
                onChange={(e) => setForm({ ...form, avg_delivery_days: Number(e.target.value) || 1 })}
                className="w-24 bg-[#06060c] border border-[#15152a] rounded-lg px-3 py-2 text-sm text-white font-mono cc-focus-ring"
              />
              <span className="text-xs text-[#a0a0b0]">days max</span>
              <InfoTip
                testId="settings-delivery-tip"
                text="This is your delivery commitment. Companies see it before ordering. If you don't deliver within this window, the company can claim a full refund — set a value you can confidently honour."
                side="bottom"
              />
            </div>
          </Field>
        </Section>

        <Section title="Pricing">
          <Field label="Price per video">
            <div className="flex items-center gap-2">
              <span className="text-[#00ff88] font-mono">$</span>
              <input
                data-testid="settings-price"
                type="number"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: Number(e.target.value) || 0 })}
                className="w-32 bg-[#06060c] border border-[#15152a] rounded-lg px-3 py-2 text-sm text-white font-mono cc-focus-ring"
              />
            </div>
          </Field>
        </Section>

        <UpsellsManager creatorId={user.id} initial={upsells} />

        <Section title="Portfolio · Files & YouTube links">
          <p className="text-[11px] text-[#606070] -mt-2">
            Drag & drop a video (mp4/webm/mov, up to 100 MB) or image, or paste a YouTube URL. Both formats appear together on your public profile.
          </p>
          <PortfolioDropzone
            onUploaded={(url) => {
              // Replace the first empty slot or append a new one.
              setForm((f) => {
                const idx = f.portfolio_urls.findIndex((u) => !u.trim());
                const copy = [...f.portfolio_urls];
                if (idx >= 0) copy[idx] = url; else copy.push(url);
                return { ...f, portfolio_urls: copy };
              });
            }}
          />
          <div className="space-y-3">
            {form.portfolio_urls.map((u, i) => {
              const ytId = extractYouTubeId(u);
              const uploaded = isUploadedFile(u);
              const isVid = uploaded && isVideoUrl(u);
              const isImg = uploaded && isImageUrl(u);
              return (
                <div key={i} className="flex gap-2">
                  <div className="w-24 h-14 rounded-md overflow-hidden border border-[#15152a] bg-[#06060c] flex items-center justify-center shrink-0 relative">
                    {ytId ? (
                      <>
                        <img src={youtubeThumb(ytId, "mqdefault")} alt="" className="w-full h-full object-cover" />
                        <span className="absolute inset-0 flex items-center justify-center bg-black/20">
                          <Play size={14} className="text-[#00ff88]" fill="currentColor" />
                        </span>
                      </>
                    ) : isImg ? (
                      <img src={absUrl(u)} alt="" className="w-full h-full object-cover" />
                    ) : isVid ? (
                      <>
                        <video src={absUrl(u)} className="w-full h-full object-cover" muted preload="metadata" />
                        <span className="absolute inset-0 flex items-center justify-center bg-black/30">
                          <Film size={14} className="text-[#00ff88]" />
                        </span>
                      </>
                    ) : (
                      <Youtube size={18} className="text-[#303050]" />
                    )}
                  </div>
                  <input
                    data-testid={`settings-portfolio-${i}`}
                    value={u}
                    readOnly={uploaded}
                    onChange={(e) => {
                      if (uploaded) return;
                      const copy = [...form.portfolio_urls];
                      copy[i] = e.target.value;
                      setForm({ ...form, portfolio_urls: copy });
                    }}
                    placeholder="https://youtube.com/watch?v=… or upload a file above"
                    className={`flex-1 bg-[#06060c] border border-[#15152a] rounded-lg px-3 py-2 text-sm text-white placeholder-[#606070] font-mono cc-focus-ring ${uploaded ? "text-[#00ff88]/90" : ""}`}
                  />
                  {(form.portfolio_urls.length > 1 || u.trim()) && (
                    <button
                      type="button"
                      data-testid={`settings-portfolio-remove-${i}`}
                      onClick={() => {
                        const next = form.portfolio_urls.filter((_, k) => k !== i);
                        setForm({ ...form, portfolio_urls: next.length ? next : [""] });
                      }}
                      className="px-3 text-[#ff4444] hover:bg-[#ff4444]/10 border border-[#15152a] rounded-lg"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              );
            })}
            <button
              type="button"
              data-testid="settings-portfolio-add"
              onClick={() => setForm({ ...form, portfolio_urls: [...form.portfolio_urls, ""] })}
              className="text-xs text-[#00ff88] hover:underline inline-flex items-center gap-1"
            >
              <Plus size={12} /> Add another link
            </button>
          </div>
        </Section>

        <div className="flex items-center justify-between sticky bottom-4">
          <button
            type="button"
            data-testid="settings-delete-account-btn"
            onClick={() => setShowDeleteConfirm(true)}
            className="inline-flex items-center gap-2 text-sm text-[#ff4444] border border-[#ff4444]/40 bg-[#ff4444]/5 hover:bg-[#ff4444]/10 rounded-lg px-4 py-3 transition-colors"
          >
            <Trash2 size={14} /> Delete account
          </button>
          <button
            data-testid="settings-save-btn"
            type="submit"
            disabled={saving}
            className="cc-btn-primary rounded-lg px-6 py-3 text-sm inline-flex items-center gap-2 disabled:opacity-50"
          >
            <Save size={14} /> {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
      {showDeleteConfirm && (
        <DeleteAccountDialog
          onClose={() => setShowDeleteConfirm(false)}
          onDeleted={async () => {
            await signOut();
            nav("/", { replace: true });
          }}
        />
      )}
    </div>
  );
}

const DeleteAccountDialog = ({ onClose, onDeleted }) => {
  const [confirm, setConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const go = async () => {
    if (confirm.trim() !== "DELETE") return;
    setDeleting(true);
    setError("");
    try {
      await deleteMyAccount();
      toast.success("Account deleted");
      onDeleted();
    } catch (e) {
      setError(e?.response?.data?.detail || "Deletion failed");
    } finally {
      setDeleting(false);
    }
  };
  return (
    <div data-testid="delete-account-dialog" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="cc-elevated rounded-2xl p-6 w-full max-w-md border border-[#ff4444]/40">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-[#ff4444]/10 flex items-center justify-center shrink-0">
            <AlertTriangle className="text-[#ff4444]" size={20} />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest font-semibold text-[#ff4444]">Irreversible</div>
            <h3 className="text-lg font-semibold text-white mt-0.5">Delete your account</h3>
          </div>
        </div>
        <p className="text-sm text-[#a0a0b0] leading-relaxed">
          Your profile, portfolio, wallet history and conversations will be permanently erased.
          Open orders must be finished or refunded first.
        </p>
        <div className="mt-4">
          <div className="cc-overline mb-1.5">Type <span className="text-white font-semibold">DELETE</span> to confirm</div>
          <input
            data-testid="delete-account-confirm"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="DELETE"
            className="w-full bg-[#06060c] border border-[#15152a] rounded-lg px-3 py-2 text-sm text-white font-mono cc-focus-ring tracking-widest"
          />
        </div>
        {error && <div className="mt-3 text-xs text-[#ff4444]">{error}</div>}
        <div className="flex items-center justify-end gap-2 mt-5">
          <button onClick={onClose} data-testid="delete-account-cancel" className="px-4 py-2 text-sm text-[#a0a0b0] hover:text-white">Cancel</button>
          <button
            data-testid="delete-account-submit"
            onClick={go}
            disabled={deleting || confirm.trim() !== "DELETE"}
            className="bg-[#ff4444] text-white rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-40"
          >
            {deleting ? "Deleting…" : "Delete forever"}
          </button>
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <section className="cc-surface rounded-2xl p-6 space-y-4">
    <div className="cc-overline">{title}</div>
    {children}
  </section>
);

const Field = ({ label, children }) => (
  <div>
    <div className="cc-overline mb-1.5">{label}</div>
    {children}
  </div>
);

const Chips = ({ options, selected, onToggle, testPrefix }) => (
  <div className="flex flex-wrap gap-1.5">
    {options.map((o) => (
      <button
        key={o}
        type="button"
        onClick={() => onToggle(o)}
        data-testid={`${testPrefix}-${o}`}
        className={`px-2.5 py-1 rounded-md text-[11px] transition ${
          selected.includes(o)
            ? "bg-[#00ff88]/10 text-[#00ff88] border border-[#00ff88]/30"
            : "border border-[#15152a] text-[#a0a0b0] hover:text-white"
        }`}
      >
        {o}
      </button>
    ))}
  </div>
);

const Toggle = ({ label, checked, onChange, testId }) => (
  <label className="flex items-center gap-2 text-sm text-[#a0a0b0] cursor-pointer" data-testid={testId}>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative w-9 h-5 rounded-full p-0.5 transition-colors ${checked ? "bg-[#00ff88]/30" : "bg-[#15152a]"}`}
    >
      <span className={`block w-4 h-4 rounded-full transition-transform ${checked ? "translate-x-4 bg-[#00ff88]" : "bg-[#606070]"}`} />
    </button>
    {label}
  </label>
);

const ACCEPT_PORTFOLIO = "video/mp4,video/webm,video/quicktime,video/x-matroska,video/x-msvideo,image/jpeg,image/png,image/webp,image/gif";

const PortfolioDropzone = ({ onUploaded }) => {
  const inputRef = useRef(null);
  const [hovering, setHovering] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [fileName, setFileName] = useState("");

  const doUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setProgress(0);
    setFileName(file.name);
    try {
      const res = await uploadPortfolio(file, (p) => setProgress(p));
      onUploaded(res.url);
      toast.success(`Uploaded · ${file.name}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Upload failed";
      toast.error(typeof msg === "string" ? msg : "Upload failed");
    } finally {
      setUploading(false);
      setProgress(0);
      setFileName("");
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setHovering(false);
    const files = Array.from(e.dataTransfer?.files || []);
    if (files[0]) doUpload(files[0]);
  };

  return (
    <div
      data-testid="settings-portfolio-dropzone"
      onDragOver={(e) => { e.preventDefault(); setHovering(true); }}
      onDragLeave={() => setHovering(false)}
      onDrop={onDrop}
      onClick={() => !uploading && inputRef.current?.click()}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !uploading) inputRef.current?.click(); }}
      className={`relative rounded-xl border-2 border-dashed transition-colors p-6 text-center cursor-pointer ${
        hovering
          ? "border-[#00ff88] bg-[#00ff88]/5"
          : "border-[#15152a] bg-[#06060c] hover:border-[#00ff88]/40"
      } ${uploading ? "pointer-events-none opacity-70" : ""}`}
    >
      <input
        ref={inputRef}
        type="file"
        data-testid="settings-portfolio-file-input"
        accept={ACCEPT_PORTFOLIO}
        className="hidden"
        onChange={(e) => doUpload(e.target.files?.[0])}
      />
      {uploading ? (
        <div className="flex flex-col items-center gap-2">
          <Loader2 size={22} className="text-[#00ff88] animate-spin" />
          <div className="text-sm text-white truncate max-w-full">{fileName}</div>
          <div className="w-full max-w-[260px] h-1.5 bg-[#15152a] rounded-full overflow-hidden">
            <div className="h-full bg-[#00ff88] transition-all" style={{ width: `${progress}%` }} />
          </div>
          <div className="text-[11px] font-mono text-[#a0a0b0]">{progress}%</div>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2">
          <UploadCloud size={26} className="text-[#00ff88]" />
          <div className="text-sm text-white">
            <span className="text-[#00ff88]">Click to browse</span> or drag a file here
          </div>
          <div className="flex items-center gap-3 text-[11px] text-[#606070]">
            <span className="inline-flex items-center gap-1"><Film size={11} /> mp4 · webm · mov</span>
            <span className="inline-flex items-center gap-1"><ImageIcon size={11} /> jpg · png · webp</span>
            <span>· up to 100 MB</span>
          </div>
        </div>
      )}
    </div>
  );
};
